/**
 * Hero capture helpers for the Legendary Arena game engine (WP-214).
 *
 * Three pure functions for the hero capture lifecycle:
 * - captureHeroFromHq: remove a hero from HQ by selector, attach to villain,
 *   refill HQ slot from hero deck
 * - awardAttachedHeroes: award captured heroes to player's discard on defeat
 * - moveAttachedHeroesToEscapedPile: captured heroes go to the Escaped Villains pile when their captor escapes (D-24657)
 *
 * All functions operate on G directly (not returning new objects) to match
 * the existing villain-effect mutation pattern. No boardgame.io import.
 * No ctx dependency. No randomness. No .reduce().
 */
import { refillHqSlot } from './city.logic.js';
/**
 * Captures a hero from the HQ and attaches it to a villain.
 *
 * Scans HQ slots by the given selector strategy, removes the selected hero,
 * attaches it to G.villainAttachedHeroes[villainCardId], and refills the HQ
 * slot from G.heroDeck. All three steps execute atomically — no intermediate
 * state is observable.
 *
 * Selector strategies:
 * - 'rightmost': scans index 4 → 0, captures first non-null slot.
 * - 'highestCost': scans left→right, captures highest G.cardStats[id].cost;
 *   ties broken by rightmost index.
 * - 'lowestCost': scans left→right, captures lowest G.cardStats[id].cost;
 *   ties broken by rightmost index.
 *
 * @param G - Game state (mutated directly).
 * @param villainCardId - The villain zone-instance ext_id to attach to.
 * @param selector - Which HQ selection strategy to apply.
 * @returns CaptureHeroResult when a hero was captured; null when HQ is empty.
 */
export function captureHeroFromHq(G, villainCardId, selector) {
    let selectedIndex = null;
    if (selector === 'rightmost') {
        // why: scan right-to-left (index 4 → 0); capture the first non-null slot
        for (let hqIndex = 4; hqIndex >= 0; hqIndex--) {
            if (G.hq[hqIndex] !== null) {
                selectedIndex = hqIndex;
                break;
            }
        }
    }
    else if (selector === 'highestCost') {
        // why: scan left-to-right; track highest cost hero; rightmost index wins ties
        let highestCost = -1;
        for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
            const heroSlot = G.hq[hqIndex];
            if (heroSlot === null || heroSlot === undefined)
                continue;
            const heroId = heroSlot;
            const heroCost = G.cardStats[heroId]?.cost ?? 0;
            if (heroCost > highestCost || (heroCost === highestCost && hqIndex > (selectedIndex ?? -1))) {
                highestCost = heroCost;
                selectedIndex = hqIndex;
            }
        }
    }
    else {
        // why: scan left-to-right; track lowest cost hero; rightmost index wins ties
        let lowestCost = Infinity;
        for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
            const heroSlot = G.hq[hqIndex];
            if (heroSlot === null || heroSlot === undefined)
                continue;
            const heroId = heroSlot;
            const heroCost = G.cardStats[heroId]?.cost ?? 0;
            if (heroCost < lowestCost || (heroCost === lowestCost && hqIndex > (selectedIndex ?? -1))) {
                lowestCost = heroCost;
                selectedIndex = hqIndex;
            }
        }
    }
    if (selectedIndex === null) {
        // why: all HQ slots are null — safe no-op per zone-exhaustion defence
        return null;
    }
    const capturedHeroId = G.hq[selectedIndex];
    // Step 1: Remove hero from HQ slot (set to null before refill)
    G.hq[selectedIndex] = null;
    // Step 2: Attach hero to villain's capture list
    const existingCaptures = G.villainAttachedHeroes[villainCardId];
    if (existingCaptures === undefined) {
        G.villainAttachedHeroes[villainCardId] = [capturedHeroId];
    }
    else {
        existingCaptures.push(capturedHeroId);
    }
    // Step 3: Refill HQ slot from hero deck
    const refillResult = refillHqSlot(G.hq, selectedIndex, G.heroDeck);
    G.hq = refillResult.hq;
    G.heroDeck = refillResult.heroDeck;
    const refilledHeroId = G.hq[selectedIndex] ?? null;
    return {
        capturedHeroId,
        hqIndex: selectedIndex,
        refilledHeroId,
    };
}
/**
 * Awards all heroes attached to a defeated villain to the player's discard pile.
 *
 * Called from fightVillain after the villain is placed in victory. Moves every
 * hero in G.villainAttachedHeroes[villainCardId] to G.playerZones[playerId].discard.
 * Deletes the mapping entry (not set to []).
 *
 * @param G - Game state (mutated directly).
 * @param villainCardId - The defeated villain zone-instance ext_id.
 * @param playerId - The defeating player's id (ctx.currentPlayer string).
 */
// why: heroes go to discard ("Gain that Hero" card text) — they enter the
// player's deck the normal way by shuffling, not into the victory pile
export function awardAttachedHeroes(G, villainCardId, playerId) {
    // why: guard for pre-WP-214 G fixtures in tests that do not include the field
    if (!G.villainAttachedHeroes)
        return;
    const capturedHeroes = G.villainAttachedHeroes[villainCardId];
    if (capturedHeroes === undefined || capturedHeroes.length === 0) {
        // why: no-op when villain has no attached heroes — backward compatible
        return;
    }
    const playerZones = G.playerZones[playerId];
    if (!playerZones)
        return;
    for (const heroId of capturedHeroes) {
        playerZones.discard.push(heroId);
    }
    // why: delete entry rather than setting to [] — zone integrity rule:
    // G.villainAttachedHeroes[v] exists only while length > 0 (D-21401)
    delete G.villainAttachedHeroes[villainCardId];
}
/**
 * Moves all heroes attached to an escaping villain into the Escaped Villains pile.
 *
 * Called from both escape paths (villainDeck.reveal.ts and the scheme-twist push
 * in schemeTwistResolvers.ts). Appends every hero in
 * G.villainAttachedHeroes[villainCardId] to G.escapedPile and deletes the mapping
 * entry (not set to []).
 *
 * @param G - Game state (mutated directly).
 * @param villainCardId - The escaping villain zone-instance ext_id.
 */
// why: D-24657 — Universal Rules v23 "Villains Escaping with Captured Heroes":
// "If a Villain escapes with captured Heroes, that doesn't cause any discarding.
// The captured Heroes just stay in the Escape Pile." They used to be KO'd. A hero
// ext_id carries no villainDeckCardTypes entry and is not a Bystander, so the
// escaped-pile scheme-loss and bystander-lost counters do not count it.
export function moveAttachedHeroesToEscapedPile(G, villainCardId) {
    // why: guard for pre-WP-214 G fixtures in tests that do not include the field
    if (!G.villainAttachedHeroes)
        return;
    const capturedHeroes = G.villainAttachedHeroes[villainCardId];
    if (capturedHeroes === undefined || capturedHeroes.length === 0) {
        // why: no-op when villain has no attached heroes — backward compatible
        return;
    }
    G.escapedPile = [...G.escapedPile, ...capturedHeroes];
    // why: delete entry rather than setting to [] — zone integrity rule:
    // G.villainAttachedHeroes[v] exists only while length > 0 (D-21401)
    delete G.villainAttachedHeroes[villainCardId];
}
//# sourceMappingURL=heroCapture.logic.js.map